/**
 * Account security settings — currently scoped to 2FA enrolment + recovery codes.
 *
 * State machine:
 *
 *   ┌─ TOTP disabled ──┐
 *   │                  │
 *   │ click "Enable"   │
 *   ▼                  │
 *   ┌─ "setup" ────────┐
 *   │ • secret shown   │
 *   │ • QR rendered    │
 *   │ • verify 6-digit │
 *   ▼                  │
 *   ┌─ "show codes" ───┐
 *   │ recovery codes   │
 *   │ shown ONCE       │
 *   │ click "Done"     │
 *   ▼                  │
 *   ┌─ TOTP enabled ───┴─────┐
 *                            │
 *   click "Disable" + verify │
 *   ▼                        ▼
 *   ┌─ TOTP disabled ────────┘
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { api, ApiError } from '../auth/api';
import { useAuthStore } from '../auth/store';

type View =
  | { kind: 'idle' }
  | { kind: 'setup'; secret: string; uri: string; qrDataUrl: string }
  | { kind: 'show-codes'; codes: string[] }
  | { kind: 'disabling' };

export function SecurityPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const refreshUser = useAuthStore((s) => s.refreshUser);

  const [view, setView] = useState<View>({ kind: 'idle' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');

  // Bounce unauthenticated visitors back to login (preserve target).
  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true, state: { from: '/security' } });
    }
  }, [token, navigate]);

  if (!token || !user) {
    return null;
  }

  const startSetup = async () => {
    setBusy(true);
    setError(null);
    try {
      const { secret, provisioning_uri } = await api.totpSetup(token);
      // Render QR client-side. SVG output keeps it sharp on hidpi screens and avoids any
      // canvas-handle headaches with React's render lifecycle.
      const qrDataUrl = await QRCode.toDataURL(provisioning_uri, {
        margin: 1,
        width: 256,
        errorCorrectionLevel: 'M',
      });
      setView({ kind: 'setup', secret, uri: provisioning_uri, qrDataUrl });
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'Failed to start 2FA setup');
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async () => {
    if (!/^\d{6}$/.test(totpCode)) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { recovery_codes } = await api.totpEnable(token, totpCode);
      setView({ kind: 'show-codes', codes: recovery_codes });
      setTotpCode('');
      await refreshUser();
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'totp_wrong'
        ? 'Wrong code. Make sure your phone clock is in sync and try the next 30-second code.'
        : 'Could not enable 2FA — try again.');
    } finally {
      setBusy(false);
    }
  };

  const finishSetup = () => {
    setView({ kind: 'idle' });
  };

  const startDisable = () => {
    setView({ kind: 'disabling' });
    setError(null);
    setDisablePassword('');
    setDisableCode('');
  };

  const confirmDisable = async () => {
    setBusy(true);
    setError(null);
    try {
      const isRecovery = /^[A-Z2-7-]{10,12}$/i.test(disableCode) && !/^\d+$/.test(disableCode);
      await api.totpDisable(token, disablePassword, isRecovery
        ? { recovery_code: disableCode }
        : { totp_code: disableCode });
      setView({ kind: 'idle' });
      await refreshUser();
    } catch (err) {
      if (err instanceof ApiError) {
        switch (err.code) {
          case 'invalid_credentials':
            setError('Wrong password.');
            break;
          case 'totp_wrong':
            setError('Wrong authenticator / recovery code.');
            break;
          default:
            setError('Could not disable 2FA.');
        }
      } else {
        setError('Network error.');
      }
    } finally {
      setBusy(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* clipboard API may be blocked; user can select+copy manually */
    }
  };

  return (
    <main className="min-h-screen p-4 sm:p-6 max-w-xl mx-auto">
      {/* Back-arrow + breadcrumb row, then big title.
       *   `← home` is the universal escape hatch (same pattern as /machines).
       *   `machines` is the natural parent in the navigation tree — Security sits as a
       *   child detail page of Machines (where you manage account-level settings).
       * Both links use the same chip styling so the visual rhythm is consistent. */}
      <header className="mb-6 space-y-3">
        <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 hover:text-[var(--color-accent)] transition-colors px-2 py-1.5 rounded -ml-1"
            aria-label="Back to home"
          >
            <span aria-hidden="true">←</span>
            <span>home</span>
          </Link>
          <span className="opacity-40" aria-hidden="true">/</span>
          <Link
            to="/machines"
            className="hover:text-[var(--color-accent)] transition-colors px-1 py-1.5 rounded"
          >
            machines
          </Link>
          <span className="opacity-40" aria-hidden="true">/</span>
          <span className="text-[var(--color-text)] px-1 py-1.5">security</span>
        </div>
        <h1 className="text-2xl font-semibold leading-tight">Security</h1>
      </header>

      <section className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded p-5 mb-6">
        <h2 className="text-lg font-semibold mb-1">Two-factor authentication</h2>
        <p className="text-sm text-[var(--color-text-muted)] mb-4">
          {user.totp_enabled
            ? `Enabled on ${user.totp_enabled_at ? new Date(user.totp_enabled_at).toLocaleDateString() : 'an earlier date'}.`
            : 'Adds a six-digit code from your phone on top of your password.'}
        </p>

        {error && (
          <div className="mb-3 px-3 py-2 rounded bg-[var(--color-danger)]/10 border border-[var(--color-danger)]/40 text-sm text-[var(--color-danger)]">
            {error}
          </div>
        )}

        {view.kind === 'idle' && !user.totp_enabled && (
          <button
            type="button"
            disabled={busy}
            onClick={startSetup}
            className="btn-gold px-5 py-2 disabled:opacity-50"
          >
            {busy ? 'Preparing…' : 'Enable 2FA'}
          </button>
        )}

        {view.kind === 'idle' && user.totp_enabled && (
          <button
            type="button"
            onClick={startDisable}
            className="border border-[var(--color-danger)] text-[var(--color-danger)] rounded-md px-5 py-2 hover:bg-[var(--color-danger)]/10 transition"
          >
            Disable 2FA
          </button>
        )}

        {view.kind === 'setup' && (
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-3">
              <img
                src={view.qrDataUrl}
                alt="Scan with your authenticator app"
                className="rounded bg-white p-2"
                width={256}
                height={256}
              />
              <p className="text-xs text-[var(--color-text-muted)]">
                Scan with Google Authenticator, 1Password, Aegis, or any RFC 6238 app.
              </p>
            </div>

            <details className="text-xs">
              <summary className="cursor-pointer text-[var(--color-text-muted)]">
                Can't scan? Show the manual entry key
              </summary>
              <div className="mt-2 p-3 bg-black/30 rounded font-mono break-all flex items-center gap-2">
                <code className="flex-1">{view.secret}</code>
                <button
                  type="button"
                  className="text-xs px-2 py-1 rounded border border-[var(--color-border)]"
                  onClick={() => copyToClipboard(view.secret)}
                >
                  copy
                </button>
              </div>
            </details>

            <div>
              <label className="block text-xs uppercase tracking-wider text-[var(--color-text-muted)] mb-2">
                Enter code to confirm
              </label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                className="w-full bg-[var(--color-surface)] border border-[var(--color-border)] rounded-md px-3 py-3 text-center text-2xl font-mono tracking-[0.4em] focus:outline-none focus:border-[var(--color-accent)]"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={confirmEnable}
                disabled={busy || totpCode.length !== 6}
                className="btn-gold flex-1 py-3 disabled:opacity-50"
              >
                {busy ? 'Verifying…' : 'Confirm'}
              </button>
              <button
                type="button"
                onClick={() => setView({ kind: 'idle' })}
                className="px-4 rounded-md border border-[var(--color-border)] hover:bg-[var(--color-border)] transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {view.kind === 'show-codes' && (
          <div className="space-y-4">
            <div className="px-3 py-2 rounded bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/40 text-sm">
              <strong>2FA enabled.</strong> Save these recovery codes somewhere safe — each can
              be used once if you lose your phone. They will <strong>not</strong> be shown again.
            </div>
            <ul className="grid grid-cols-2 gap-2 font-mono text-sm">
              {view.codes.map((c) => (
                <li key={c} className="px-3 py-2 bg-black/30 rounded text-center">
                  {c}
                </li>
              ))}
            </ul>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => copyToClipboard(view.codes.join('\n'))}
                className="flex-1 border border-[var(--color-border)] rounded-md py-2 hover:bg-[var(--color-border)] transition text-sm"
              >
                Copy all
              </button>
              <button
                type="button"
                onClick={finishSetup}
                className="btn-gold flex-1 py-2"
              >
                I saved them
              </button>
            </div>
          </div>
        )}

        {view.kind === 'disabling' && (
          <div className="space-y-3">
            <p className="text-sm text-[var(--color-text-muted)]">
              Verify with your password and a current 2FA code (or any unused recovery code).
            </p>
            <input
              type="password"
              autoComplete="current-password"
              placeholder="Account password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              className="w-full bg-[var(--color-surface)] border border-[var(--color-border)] rounded-md px-3 py-3 text-sm focus:outline-none focus:border-[var(--color-accent)]"
            />
            <input
              type="text"
              autoComplete="off"
              placeholder="6-digit code or XXXXX-XXXXX recovery code"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              className="w-full bg-[var(--color-surface)] border border-[var(--color-border)] rounded-md px-3 py-3 text-sm font-mono focus:outline-none focus:border-[var(--color-accent)]"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={confirmDisable}
                disabled={busy || !disablePassword || !disableCode}
                className="flex-1 border border-[var(--color-danger)] text-[var(--color-danger)] rounded-md py-3 hover:bg-[var(--color-danger)]/10 disabled:opacity-50 transition"
              >
                {busy ? 'Disabling…' : 'Disable 2FA'}
              </button>
              <button
                type="button"
                onClick={() => setView({ kind: 'idle' })}
                className="px-4 rounded-md border border-[var(--color-border)] hover:bg-[var(--color-border)] transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
